import { useState } from 'react'
import { cn } from '../lib/cn'

interface Props {
  height?: number
  variant?: 'light' | 'dark'
  showWordmark?: boolean
  className?: string
}

/**
 * Renders the CyLogic master mark + "CyLogic" wordmark.
 *
 * The mark image is scraped from cylogic.com at build time and cached at
 * /assets/cylogic-mark.png. If the image fails to load (scrape failed,
 * cache miss), the component falls back to a typographic wordmark only.
 *
 * `variant="dark"` is for navy / dark backgrounds (white text).
 * `variant="light"` is for chalk / white backgrounds (navy text).
 */
export function Logo({ height = 32, variant = 'dark', showWordmark = true, className }: Props) {
  const [imgFailed, setImgFailed] = useState(false)
  const textColor = variant === 'dark' ? 'text-white' : 'text-navy'

  return (
    <div className={cn('inline-flex items-center gap-2', className)}>
      {!imgFailed && (
        <img
          src="/assets/cylogic-mark.png"
          alt=""
          aria-hidden="true"
          width={height}
          height={height}
          onError={() => setImgFailed(true)}
          className="rounded-sm"
          style={{ height: `${height}px`, width: `${height}px` }}
        />
      )}
      {showWordmark && (
        <span
          className={cn('font-semibold tracking-tight', textColor)}
          style={{
            fontFamily: 'Lexend, system-ui, sans-serif',
            fontSize: `${Math.round(height * 0.6)}px`,
            letterSpacing: '-0.02em',
          }}
        >
          CyLogic
        </span>
      )}
    </div>
  )
}
